// Service for handling TLDR operations with Firebase
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy, limit, onSnapshot, or } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getEmailById } from './emailService';

const TLDRS_COLLECTION = 'tldrs';

// Create a new TLDR in Firebase
export const createTLDR = async (tldrData, userId) => {
  try {
    const tldrsRef = collection(db, TLDRS_COLLECTION);
    
    // 1. Check if TLDR already exists for this email
    if (tldrData.emailId) {
      const emailIdQuery = query(tldrsRef, where("emailId", "==", tldrData.emailId), where("userId", "==", userId));
      const emailIdSnapshot = await getDocs(emailIdQuery);
      
      if (!emailIdSnapshot.empty) {
        console.log(`TLDR already exists for email ${tldrData.emailId}`);
        return emailIdSnapshot.docs[0].id;
      }
    }
    
    // 2. DISABLED: Check if there's a very similar TLDR with the same subject (for this user)
    // if (tldrData.emailSubject && userId) {
    //   const subjectQuery = query(
    //     tldrsRef, 
    //     where("emailSubject", "==", tldrData.emailSubject),
    //     where("userId", "==", userId)
    //   );
    //   const subjectSnapshot = await getDocs(subjectQuery);
    //   
    //   if (!subjectSnapshot.empty) {
    //     console.log(`TLDR with same subject "${tldrData.emailSubject}" already exists for this user`);
    //     return subjectSnapshot.docs[0].id;
    //   }
    // }
    
    // 3. Check if there's a TLDR with identical summary (for this user)
    if (tldrData.summary && userId) {
      const summaryQuery = query(
        tldrsRef,
        where("userId", "==", userId)
      );
      const summarySnapshot = await getDocs(summaryQuery);
      
      // Check all summaries for this user for similarity
      const existingSummaries = summarySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        summary: doc.data().summary 
      }));
      
      for (let i = 0; i < existingSummaries.length; i++) {
        const existingSummary = existingSummaries[i]?.summary?.trim()?.toLowerCase();
        const newSummary = tldrData.summary?.trim()?.toLowerCase();
        
        if (existingSummary && newSummary && existingSummary === newSummary) {
          console.log(`TLDR with identical summary already exists for this user`);
          return existingSummaries[i].id;
        }
        
        // Also check for high similarity using the same algorithm as deduplicateTLDRs
        if (existingSummary && newSummary) {
          const similarity = calculateSimilarity(newSummary, existingSummary);
          if (similarity >= 0.6) { // 80% similarity threshold
            console.log(`TLDR with similar summary (${Math.round(similarity * 100)}% match) already exists for this user`);
            return existingSummaries[i].id;
          }
        }
      }
    }
    
    // No duplicate found, add new TLDR with userId
    const docRef = await addDoc(tldrsRef, {
      ...tldrData,
      userId: userId,
      createdAt: new Date(),
      isRead: false
    });
    
    console.log(`TLDR created with ID: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Error creating TLDR:', error);
    throw error;
  }
};

// Get all TLDRs (with optional filters)
export const getTLDRs = async (filters = {}, userId) => {
  try {
    const tldrsRef = collection(db, TLDRS_COLLECTION);
    
    // Create conditions for current user AND shared users
    const userConditions = [where("userId", "==", userId)];
    
    // Add shared user conditions if provided
    if (filters.sharedUserIds && filters.sharedUserIds.length > 0) {
      filters.sharedUserIds.forEach(sharedUserId => {
        userConditions.push(where("userId", "==", sharedUserId));
      });
    }
    
    // Create query with OR condition
    let q = userConditions.length > 1 
      ? query(tldrsRef, or(...userConditions))
      : query(tldrsRef, userConditions[0]);
    
    // Apply filters
    if (filters.isRead !== undefined) {
      q = query(q, where("isRead", "==", filters.isRead));
    }
    
    if (filters.category) {
      q = query(q, where("category", "==", filters.category));
    }
    
    if (filters.priority) {
      q = query(q, where("priority", "==", filters.priority));
    }
    
    // Order by creation date (newest first)
    q = query(q, orderBy("createdAt", "desc"));
    
    // Apply limit if specified
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const querySnapshot = await getDocs(q);
    const tldrs = [];
    
    querySnapshot.forEach((doc) => {
      tldrs.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return tldrs;
  } catch (error) {
    console.error('Error getting TLDRs:', error);
    throw error;
  }
};

// Get recent TLDRs (last N days)
export const getRecentTLDRs = async (daysBack = 7, limitCount = 50, userId, sharedUserIds = []) => {
  try {
    const tldrsRef = collection(db, TLDRS_COLLECTION);
    const dateThreshold = new Date();
    dateThreshold.setDate(dateThreshold.getDate() - daysBack);
    
    // Create conditions for current user AND shared users
    const userConditions = [where("userId", "==", userId)];
    
    // Add shared user conditions if provided
    if (sharedUserIds && sharedUserIds.length > 0) {
      sharedUserIds.forEach(sharedUserId => {
        userConditions.push(where("userId", "==", sharedUserId));
      });
    }
    
    // Create query with OR condition
    const q = userConditions.length > 1
      ? query(
          tldrsRef,
          or(...userConditions),
          where("createdAt", ">", dateThreshold),
          orderBy("createdAt", "desc"),
          limit(limitCount)
        )
      : query(
          tldrsRef,
          userConditions[0],
          where("createdAt", ">", dateThreshold),
          orderBy("createdAt", "desc"),
          limit(limitCount)
        );
    
    const querySnapshot = await getDocs(q);
    const tldrs = [];
    
    querySnapshot.forEach((doc) => {
      tldrs.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return tldrs;
  } catch (error) {
    console.error('Error getting recent TLDRs:', error);
    throw error;
  }
};

// Subscribe to TLDRs updates (real-time)
export const subscribeTLDRs = (callback, userId, filters = {}) => {
  const tldrsRef = collection(db, TLDRS_COLLECTION);
  
  // Create conditions for current user AND shared users
  const userConditions = [where("userId", "==", userId)];
  
  // Add shared user conditions if provided
  if (filters.sharedUserIds && filters.sharedUserIds.length > 0) {
    filters.sharedUserIds.forEach(sharedUserId => {
      userConditions.push(where("userId", "==", sharedUserId));
    });
  }
  
  // Create query with OR condition
  let q = userConditions.length > 1 
    ? query(tldrsRef, or(...userConditions))
    : query(tldrsRef, userConditions[0]);
  
  // Apply filters
  if (filters.isRead !== undefined) {
    q = query(q, where("isRead", "==", filters.isRead));
  }
  
  // Order by creation date
  q = query(q, orderBy("createdAt", "desc"));
  
  // Apply limit if specified
  if (filters.limit) {
    q = query(q, limit(filters.limit));
  }
  
  // Subscribe to changes
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const tldrs = [];
    
    querySnapshot.forEach((doc) => {
      tldrs.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    callback(tldrs);
  });
  
  return unsubscribe;
};

/**
 * Calculate similarity between two strings using Jaccard similarity coefficient
 * @param {string} str1 First string to compare
 * @param {string} str2 Second string to compare
 * @returns {number} Similarity score between 0-1 where 1 is identical
 */
function calculateSimilarity(str1, str2) {
  // Guard against empty strings
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 1;
  
  // Convert strings to lowercase and split into words
  const words1 = str1.toLowerCase().split(/\s+/);
  const words2 = str2.toLowerCase().split(/\s+/);
  
  // Create sets of unique words
  const set1 = new Set(words1);
  const set2 = new Set(words2);
  
  // Calculate intersection
  const intersection = new Set([...set1].filter(word => set2.has(word)));
  
  // Calculate union
  const union = new Set([...set1, ...set2]);
  
  // Jaccard similarity: intersection size / union size
  return intersection.size / union.size;
}

/**
 * Extract key information from a TLDR summary
 * This helps match similar TLDRs that might have different wording
 * @param {string} summary The TLDR summary text
 * @returns {object} Object with extracted dates, numbers, names, etc.
 */
function extractKeyInformation(summary) {
  if (!summary) return { dates: [], numbers: [] };
  
  // Extract dates in various formats (e.g., June 4, June 19, May 30)
  const dateRegex = /(?:\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December))|(?:(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}(?:st|nd|rd|th)?)/gi;
  const dates = summary.match(dateRegex) || [];
  
  // Extract numbers (likely to be important in educational context)
  const numberRegex = /\d+/g;
  const numbers = summary.match(numberRegex) || [];
  
  return {
    dates: dates.map(date => date.toLowerCase()),
    numbers
  };
}

/**
 * Deduplicates TLDRs based on similarity of content and subject
 * @param {Array} tldrs Array of TLDR objects 
 * @returns {Object} Object containing deduplicated array of TLDRs and metadata about skipped duplicates
 */
function deduplicateTLDRs(tldrs) {
  if (!tldrs || !Array.isArray(tldrs) || tldrs.length === 0) {
    return { uniqueTLDRs: [], duplicationMetadata: { duplicatesFound: 0, details: [] } };
  }
  
  // Sort TLDRs by date (newest first)
  const sortedTLDRs = [...tldrs].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });
  
  // Group TLDRs by similar content
  const uniqueTLDRs = [];
  const seenSubjects = new Map(); // Store original TLDR id with the subject
  const seenSummaries = new Map(); // Store original TLDR id with the summary
  
  // Keep track of duplicates for UI display
  const skippedDuplicates = [];
  
  // Configure similarity thresholds
  const SIMILAR_SUMMARY_THRESHOLD = 0.8; // 80% similarity is considered a duplicate
  
  for (const tldr of sortedTLDRs) {
    // Skip TLDRs without summary (should not happen)
    if (!tldr.summary) {
      uniqueTLDRs.push(tldr);
      continue;
    }
    
    // Check for duplicates - only analyze summary content
    let isDuplicate = false;
    let duplicateReason = '';
    let originalTLDRId = null;
    let similarityScore = 0;
    
    // DISABLED: First check for duplicate subjects
    // if (tldr.emailSubject) {
    //   const normalizedSubject = tldr.emailSubject.trim().toLowerCase();
    //   if (seenSubjects.has(normalizedSubject)) {
    //     originalTLDRId = seenSubjects.get(normalizedSubject);
    //     duplicateReason = 'subject';
    //     console.log(`Skipping duplicate TLDR with subject: "${tldr.emailSubject}"`);
    //     isDuplicate = true;
    //   } else {
    //     seenSubjects.set(normalizedSubject, tldr.id);
    //   }
    // }
    
    // Check for exact duplicate summaries
    const normalizedSummary = tldr.summary.trim().toLowerCase();
    if (seenSummaries.has(normalizedSummary)) {
      originalTLDRId = seenSummaries.get(normalizedSummary);
      duplicateReason = 'identical summary';
      console.log(`Skipping duplicate TLDR with identical summary`);
      isDuplicate = true;
    } else {
      seenSummaries.set(normalizedSummary, tldr.id);
    }
    
    // If not an exact duplicate, check for similar summaries using fuzzy matching
    if (!isDuplicate) {
      const currentSummary = tldr.summary.trim().toLowerCase();
      
      // Check against all unique TLDRs we've collected so far
      for (const uniqueTLDR of uniqueTLDRs) {
        if (!uniqueTLDR.summary) continue;
        
        const existingSummary = uniqueTLDR.summary.trim().toLowerCase();
        
        // Calculate similarity using Jaccard coefficient (no date boosting)
        const similarity = calculateSimilarity(currentSummary, existingSummary);
        
        // If similarity is above threshold, consider it a duplicate
        if (similarity >= SIMILAR_SUMMARY_THRESHOLD) {
          isDuplicate = true;
          duplicateReason = 'similar summary';
          originalTLDRId = uniqueTLDR.id;
          similarityScore = similarity;
          console.log(`Skipping similar TLDR with similarity score ${similarity.toFixed(2)}`);
          break;
        }
      }
    }
    
    // If not a duplicate, add to unique list
    if (!isDuplicate) {
      uniqueTLDRs.push(tldr);
    } else {
      // Store information about the skipped duplicate
      skippedDuplicates.push({
        id: tldr.id,
        originalId: originalTLDRId,
        reason: duplicateReason,
        subject: tldr.emailSubject || 'No subject',
        createdAt: tldr.createdAt,
        similarityScore: similarityScore || 1 // 1 for exact matches
      });
    }
  }
  
  console.log(`TLDR deduplication: ${tldrs.length} → ${uniqueTLDRs.length} (skipped ${skippedDuplicates.length} duplicates)`);
  
  // Return both the deduplicated TLDRs and metadata about what was skipped
  return {
    uniqueTLDRs,
    duplicationMetadata: {
      duplicatesFound: skippedDuplicates.length,
      details: skippedDuplicates
    }
  };
}

// Subscribe to TLDRs with email bodies
export const subscribeTLDRsWithEmails = (callback, userId, filters = {}) => {
  const tldrsRef = collection(db, TLDRS_COLLECTION);
  
  // Create conditions for current user AND shared users
  const userConditions = [where("userId", "==", userId)];
  
  // Add shared user conditions if provided
  if (filters.sharedUserIds && filters.sharedUserIds.length > 0) {
    filters.sharedUserIds.forEach(sharedUserId => {
      userConditions.push(where("userId", "==", sharedUserId));
    });
  }
  
  // Create query with OR condition
  let q = userConditions.length > 1 
    ? query(tldrsRef, or(...userConditions))
    : query(tldrsRef, userConditions[0]);
  
  // Apply filters
  if (filters.isRead !== undefined) {
    q = query(q, where("isRead", "==", filters.isRead));
  }
  
  // Order by creation date
  q = query(q, orderBy("createdAt", "desc"));
  
  // Apply limit if specified
  if (filters.limit) {
    q = query(q, limit(filters.limit));
  }
  
  // Also set up listener for Arbor1 TLDRs
  let unsubscribeArbor1 = null;
  let currentPersonalTldrs = [];
  let currentClassTldrs = [];
  
  const processAllTldrs = async (personalTldrs, classTldrs) => {
    const allTldrs = [...personalTldrs, ...classTldrs];
    
    // Sort TLDRs by email received date (most recent first) before deduplication
    const sortedTldrs = allTldrs.sort((a, b) => {
      // Use email received date if available, otherwise fall back to TLDR creation date
      const dateA = a.emailReceivedDate ? new Date(a.emailReceivedDate) : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const dateB = b.emailReceivedDate ? new Date(b.emailReceivedDate) : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      
      return dateB - dateA; // Sort descending (newest first)
    });
    
    // Deduplicate TLDRs before sending them to the callback
    const { uniqueTLDRs, duplicationMetadata } = deduplicateTLDRs(sortedTldrs);
    
    // Pass both the deduplicated TLDRs and metadata about duplicates to the callback
    callback(uniqueTLDRs, duplicationMetadata);
  };
  
  // Set up Arbor1 listener
  const setupArbor1Listener = async () => {
    try {
      // Get user's class ID from their document
      const usersRef = collection(db, 'users');
      const userQuery = query(usersRef, where('uid', '==', userId));
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        const userClassID = userData.classID;
        
        if (userClassID === 'ArborSchool-1ST(Grade One)') {
          const arbor1TLDRsRef = collection(db, 'Arbor1TLDRs');
          const arbor1Query = query(
            arbor1TLDRsRef, 
            where('classID', '==', userClassID)
          );
          
          unsubscribeArbor1 = onSnapshot(arbor1Query, (snapshot) => {
            currentClassTldrs = [];
            snapshot.forEach(doc => {
              currentClassTldrs.push({
                id: doc.id,
                ...doc.data(),
                isClassTLDR: true,
                collectionSource: 'Arbor1TLDRs'
              });
            });
            
            console.log(`📚 Fetched ${snapshot.size} shared class TLDRs from Arbor1TLDRs`);
            processAllTldrs(currentPersonalTldrs, currentClassTldrs);
          });
        }
      }
    } catch (error) {
      console.error('Error setting up Arbor1 TLDR listener:', error);
    }
  };
  
  // Subscribe to personal TLDRs
  const unsubscribePersonal = onSnapshot(q, async (querySnapshot) => {
    const tldrs = [];
    
    for (const doc of querySnapshot.docs) {
      const tldrData = {
        id: doc.id,
        ...doc.data()
      };
      
      // Get the email body from the emails collection
      if (tldrData.emailId) {
        try {
          const emailsRef = collection(db, 'emails');
          
          // First try with both emailId and userId
          let emailQuery = query(
            emailsRef, 
            where("id", "==", tldrData.emailId),
            where("userId", "==", tldrData.userId || userId)
          );
          let emailSnapshot = await getDocs(emailQuery);
          
          // If not found, try without userId constraint (for backwards compatibility)
          if (emailSnapshot.empty) {
            console.log(`No email found with userId constraint, trying without userId for emailId: ${tldrData.emailId}`);
            emailQuery = query(
              emailsRef, 
              where("id", "==", tldrData.emailId)
            );
            emailSnapshot = await getDocs(emailQuery);
          }
          
          if (!emailSnapshot.empty) {
            const emailDoc = emailSnapshot.docs[0];
            const emailData = emailDoc.data();
            tldrData.emailBody = emailData.body;
            tldrData.emailReceivedDate = emailData.date; // Add Gmail received date
            console.log(`Fetched email data for TLDR ${tldrData.id}, body length: ${emailData.body?.length || 0}, received: ${emailData.date}`);
          } else {
            console.log(`No email found for TLDR ${tldrData.id} with emailId: ${tldrData.emailId}`);
            
            // Log more details for debugging
            console.log('TLDR data:', {
              tldrId: tldrData.id,
              emailId: tldrData.emailId,
              tldrUserId: tldrData.userId,
              currentUserId: userId
            });
          }
        } catch (error) {
          console.error('Error fetching email body for TLDR:', error);
        }
      }
      
      tldrs.push(tldrData);
    }
    
    currentPersonalTldrs = tldrs;
    processAllTldrs(currentPersonalTldrs, currentClassTldrs);
  });
  
  // Set up Arbor1 listener after a short delay
  setTimeout(() => {
    setupArbor1Listener();
  }, 500);
  
  // Return cleanup function
  return () => {
    unsubscribePersonal();
    if (unsubscribeArbor1) {
      unsubscribeArbor1();
    }
  };
};

// Mark TLDR as read
export const markTLDRAsRead = async (tldrId) => {
  try {
    const tldrRef = doc(db, TLDRS_COLLECTION, tldrId);
    await updateDoc(tldrRef, {
      isRead: true,
      readAt: new Date()
    });
    
    console.log(`TLDR ${tldrId} marked as read`);
    return true;
  } catch (error) {
    console.error('Error marking TLDR as read:', error);
    throw error;
  }
};

// Delete a TLDR
export const deleteTLDR = async (tldrId) => {
  try {
    const tldrRef = doc(db, TLDRS_COLLECTION, tldrId);
    await deleteDoc(tldrRef);
    
    console.log(`TLDR ${tldrId} deleted`);
    return true;
  } catch (error) {
    console.error('Error deleting TLDR:', error);
    throw error;
  }
};

// Get TLDR by email ID
export const getTLDRByEmailId = async (emailId, userId) => {
  try {
    const tldrsRef = collection(db, TLDRS_COLLECTION);
    const q = query(tldrsRef, where("emailId", "==", emailId), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting TLDR by email ID:', error);
    throw error;
  }
};

// Extract TLDR from email content using AI
export const extractTLDRFromEmail = async (emailBody, emailSubject, API_KEY) => {
  try {
    console.log('Extracting TLDR from Seesaw email...');
    
    // Prepare the prompt for TLDR extraction
    const systemPrompt = `You are a helpful assistant that creates structured TLDR summaries of school update emails from Seesaw.

Create a TLDR that follows this format:
1. A brief title line with the week/date and year group (only include the year/grade level if it's explicitly mentioned in the email)
2. Bullet points for key learning activities (2-4 points)
3. Upcoming Events section with emoji and formatted dates
4. Action items with emojis and deadlines
5. Important reminders for parents

Focus on what parents need to know or act on, especially dates and deadlines.
IMPORTANT: Never use quotation marks in your response. For book titles, event names, or any other items that would normally have quotes, write them without quotes (e.g., "The Zuggler" should be written as: The Zuggler).

Structure the response exactly like this example:

**Week of 12 May Update (Year 1):**

- Kids wrote and published stories about their dream homes.
- In Maths, they wrapped up learning about fractions with games and activities.
- The current project explored international toys, with students presenting cultural findings.
- They designed Rube Goldberg machines in MSC to build teamwork and problem-solving.
- **Upcoming Events:**
    - 📅 *June 4*: Project Showcase - Sustainable Toymaker.
    - 📅 *June 19*: Year 1 show - An Exciting Summer Adventure.
    - 📅 *June 20*: Whole school Inspired by Nature showcase.
    - 📅 *June 23*: Field trip to OliOli.
- 🖼 *By May 30*: Submit Father's Day photo.
- 📚 Author Charlotte Jones visiting Monday 27th May - The Zuggler available for 40 AED.
- 📚 Encourage daily reading at home.
- 📝 Week of May 26: Child will present a home learning instructional task.
- 🧪 Phonics Screening happening soon - practice at home.

Return a JSON object with:
{
  "tldr": "The formatted summary as shown above",
  "category": "weekly-update" | "announcement" | "activity" | "reminder" | "urgent" | "event",
  "priority": "high" | "medium" | "low",
  "tags": ["relevant", "tags", "here"]
}`;

    const userContent = `Email Subject: ${emailSubject}

Email Body:
${emailBody}`;

    // Call OpenAI API
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: systemPrompt
          },
          {
            role: "user",
            content: userContent
          }
        ],
        temperature: 0.1,
        max_tokens: 1500,
        response_format: { type: "json_object" }
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
    }
    
    const data = await response.json();
    let result;
    
    try {
      result = JSON.parse(data.choices[0].message.content);
    } catch (parseError) {
      console.error('Failed to parse TLDR JSON:', data.choices[0].message.content);
      console.error('Parse error:', parseError);
      
      // Try to extract the content with a more forgiving approach
      const content = data.choices[0].message.content;
      
      // Check if the response was truncated
      if (content.length > 0 && !content.trim().endsWith('}')) {
        console.error('Response appears to be truncated');
        throw new Error('TLDR response was truncated. The email may be too long.');
      }
      
      // Attempt to fix common JSON issues
      try {
        // Replace smart quotes with regular quotes
        const fixedContent = content
          .replace(/[""]/g, '"')
          .replace(/['']/g, "'");
        
        result = JSON.parse(fixedContent);
      } catch (secondError) {
        // If still failing, return a fallback structure
        throw new Error(`Failed to parse TLDR response: ${parseError.message}`);
      }
    }
    
    console.log('TLDR extracted:', result.tldr);
    return result;
  } catch (error) {
    console.error('Error extracting TLDR:', error);
    throw error;
  }
};